/** Long (yearly) plans: warn 30d, urgent 7d. Short (≤60d left): warn 7d, urgent 3d. */
export const SUBSCRIPTION_WARN_DAYS_LONG = 30;
export const SUBSCRIPTION_URGENT_DAYS_LONG = 7;
export const SUBSCRIPTION_SHORT_PLAN_MAX_DAYS = 60;
export const SUBSCRIPTION_WARN_DAYS_SHORT = 7;
export const SUBSCRIPTION_URGENT_DAYS_SHORT = 3;

/** Academic year April 1 – March 31. Default subscription end = upcoming March 31. */
export function defaultAcademicYearEndDate(fromDate = new Date()) {
  const d = new Date(fromDate);
  const year = d.getFullYear();
  const month = d.getMonth();
  const endYear = month < 3 ? year : year + 1;
  return `${endYear}-03-31`;
}

/** Trial / 1-month default: 30 days from today. */
export function defaultTrialEndDate(fromDate = new Date()) {
  const d = new Date(fromDate);
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

/** Service period for invoices from subscription end (academic year or custom). */
export function getAcademicYearPeriod(subscriptionEndDate) {
  const end = String(subscriptionEndDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    const today = new Date().toISOString().slice(0, 10);
    return { periodStart: today, periodEnd: today };
  }
  const [y, m, d] = end.split('-').map(Number);
  if (m === 3 && d === 31) {
    const startYear = y - 1;
    return {
      periodStart: `${startYear}-04-01`,
      periodEnd: end,
    };
  }
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return {
    periodStart: start.toISOString().slice(0, 10),
    periodEnd: end,
  };
}

export function formatSubscriptionDate(isoDate) {
  if (!isoDate) return '—';
  const s = String(isoDate).slice(0, 10);
  const [y, m, da] = s.split('-');
  if (!y || !m || !da) return s;
  return `${da}/${m}/${y}`;
}

export function isSubscriptionExpired(isoDate) {
  if (!isoDate) return false;
  const end = new Date(String(isoDate).slice(0, 10) + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today > end;
}

export function daysUntilExpiry(isoDate) {
  if (!isoDate) return null;
  const end = new Date(String(isoDate).slice(0, 10) + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((end - today) / (24 * 60 * 60 * 1000));
}

export function getWarnThresholds(isoDate) {
  const days = daysUntilExpiry(isoDate);
  const shortPlan =
    days !== null && days <= SUBSCRIPTION_SHORT_PLAN_MAX_DAYS;
  return {
    warnDays: shortPlan ? SUBSCRIPTION_WARN_DAYS_SHORT : SUBSCRIPTION_WARN_DAYS_LONG,
    urgentDays: shortPlan ? SUBSCRIPTION_URGENT_DAYS_SHORT : SUBSCRIPTION_URGENT_DAYS_LONG,
  };
}

export function canWriteWithSubscription(isoDate) {
  return !isSubscriptionExpired(isoDate);
}

/** Read expiry from saved session (offline sync guard). */
export function getStoredSubscriptionExpiresAt() {
  try {
    const raw = localStorage.getItem('nmhelper_user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?.subscription_expires_at
      ? String(user.subscription_expires_at).slice(0, 10)
      : null;
  } catch {
    return null;
  }
}

/** @returns {{ show: boolean, days: number, urgent: boolean, expired: boolean } | null} */
export function getSubscriptionWarning(isoDate) {
  if (!isoDate) return null;
  if (isSubscriptionExpired(isoDate)) {
    return { show: true, days: 0, urgent: true, expired: true };
  }
  const days = daysUntilExpiry(isoDate);
  if (days === null) return null;
  const { warnDays, urgentDays } = getWarnThresholds(isoDate);
  if (days > warnDays) return null;
  return { show: true, days, urgent: days <= urgentDays, expired: false };
}
